import { useAutoAnimate } from "@formkit/auto-animate/react";
import {
  useMemberAsMemberNew,
  useMutationMemberInvoicePdfAsMember,
  useMutationPayInvoiceAsMember,
  useQueryInvoiceAsMember,
} from "@gymflow/api";
import { downloadPdfBlob, formatCurrency } from "@gymflow/helpers";
import { usePortalRoutes } from "apps/portal/src/hooks";
import { RouteFeature } from "apps/portal/src/routes";
import { DateTime } from "luxon";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import useScaModal from "../../../hooks/useScaModal";
import { useClubSettings } from "../../../providers";
import useGymflowModels from "../../../store";
import { Button, DownloadIcon } from "../../atoms";
import { PaymentMethodPicker } from "../../molecules/PaymentMethodPicker";
import { HostedPagesBackHeader } from "../components/HostedPagesBackHeader";
import useRedirectIfNotAuthenticated from "../useRedirectIfNotAuthenticated";
import { InvoiceBadge } from "./components/InvoiceBadge";

export const HostedPagesReceiptOverview = () => {
  const { t } = useTranslation();
  const { createSiteOrEmbedLink } = usePortalRoutes();
  useRedirectIfNotAuthenticated(
    createSiteOrEmbedLink(
      RouteFeature.SiteBuyMembershipType.replace(":membershipType", "all"),
    ),
  );
  const { api } = useGymflowModels();
  const settings = useClubSettings();
  const [parent] = useAutoAnimate();
  const { invoiceNumber } = useParams<{ invoiceNumber: string }>();
  const { data: invoice } = useQueryInvoiceAsMember({
    api,
    invoiceNumber,
  });

  const { mutateAsync: payInvoice } = useMutationPayInvoiceAsMember({
    api,
  });
  const showScaModal = useScaModal({ asMember: true });
  const [currentPaymentMethodId, setCurrentPaymentMethodId] =
    useState<string>();
  const { data: userMember } = useMemberAsMemberNew({
    api,
  });
  const { mutateAsync: mutateAsyncInvoicePdf } =
    useMutationMemberInvoicePdfAsMember({ api });

  return (
    <div className="track-height flex size-full flex-col items-center">
      <HostedPagesBackHeader
        label={t("pages.hostedPagesProfileReceiptsOverview.header")}
        rightSideOptions={
          invoice ? (
            <Button
              onClick={async () => {
                const { data } = await mutateAsyncInvoicePdf({ invoiceNumber });
                downloadPdfBlob(data, `Gymflow-${invoiceNumber}.pdf`);
              }}
              className="gap-1.5"
            >
              <DownloadIcon pathClassName="stroke-gray-950 dark:stroke-gray-25" />
              {t("pages.hostedPagesProfileReceipts.download")}
            </Button>
          ) : undefined
        }
      />
      <div
        ref={parent}
        className="flex h-fit w-full max-w-lg flex-col gap-4 px-4 py-6"
      >
        {invoice && (
          <div className="flex w-full flex-col items-center gap-y-3">
            <div className="dark:text-gray-25 text-3xl font-bold text-gray-950">
              {formatCurrency(invoice.totalAmount, settings.defaultCurrency)}
            </div>
            <div className="flex flex-col items-center gap-1">
              <InvoiceBadge invoice={invoice} />
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {DateTime.fromISO(
                  invoice.issueDate ?? new Date().toISOString(),
                ).toLocaleString(DateTime.DATE_MED)}
              </div>
            </div>
            {invoice.status === "PAST_DUE" && (
              <Button
                disabled={!currentPaymentMethodId}
                onClick={async () => {
                  await payInvoice({
                    invoiceNumber: invoice.number,
                    paymentMethodId: currentPaymentMethodId,
                  });
                  showScaModal({ invoiceNumber: invoice.number });
                }}
              >
                {t("pages.hostedPagesProfileReceiptsOverview.pay")}
              </Button>
            )}
            {invoice.status === "AWAITING_AUTHORIZATION" && (
              <Button
                onClick={async () => {
                  await payInvoice({
                    invoiceNumber: invoice.number,
                    paymentMethodId: currentPaymentMethodId,
                  });
                  showScaModal({ invoiceNumber: invoice.number });
                }}
              >
                {t("pages.hostedPagesProfileReceiptsOverview.authorize")}
              </Button>
            )}
          </div>
        )}
        {userMember && invoice && invoice?.status !== "PAID" && (
          <div className="max-w-full">
            <PaymentMethodPicker
              userMemberId={userMember.id}
              currentPaymentMethodId={currentPaymentMethodId}
              onChange={setCurrentPaymentMethodId}
              disabled={invoice?.status !== "PAST_DUE"}
              allowBankPayment
              asMember
            />
          </div>
        )}
        {!!invoice && invoice.promotionCode && (
          <div className="flex flex-row items-center justify-between">
            <div className="text-base text-gray-500 dark:text-gray-400">
              {t("pages.hostedPagesProfileReceiptsOverview.promoCode")}
            </div>
            <div className="dark:text-gray-25 text-base font-bold text-gray-950">
              &quot;{invoice.promotionCode}&quot;
            </div>
          </div>
        )}
        <div className="flex w-full border-b-2 border-dashed border-gray-400 dark:border-gray-600" />
        <div className="flex w-full flex-col gap-2">
          {!!invoice && "subscriptionName" in invoice && (
            <div className="flex w-full flex-row items-center justify-between text-gray-500 dark:text-gray-400">
              <div className="text-lg font-semibold">
                {invoice.subscriptionName}
              </div>
              <div className="text-lg font-semibold">
                {formatCurrency(
                  invoice.subscriptionPrice,
                  settings.defaultCurrency,
                )}
              </div>
            </div>
          )}
          {!!invoice && "appointableName" in invoice && (
            <div className="flex w-full flex-row items-center justify-between text-gray-500 dark:text-gray-400">
              <div className="text-lg font-semibold">
                {invoice.appointableName}
              </div>
              <div className="text-lg font-semibold">
                {formatCurrency(invoice.totalAmount, settings.defaultCurrency)}
              </div>
            </div>
          )}
          {!!invoice &&
            "productOrderItems" in invoice &&
            invoice.productOrderItems.map((item, i) => (
              <div
                key={i}
                className="flex w-full flex-row items-center justify-between text-gray-500 dark:text-gray-400"
              >
                <div className="text-lg font-semibold">
                  {item.productName}
                  {item.quantity > 1 ? ` x ${item.quantity}` : ""}
                </div>
                <div className="text-lg font-semibold">
                  {formatCurrency(item.amount, settings.defaultCurrency)}
                </div>
              </div>
            ))}
          {!!invoice && "feeReason" in invoice && (
            <div className="flex w-full flex-row items-center justify-between text-gray-500 dark:text-gray-400">
              <div className="text-lg font-semibold">
                {t(
                  `pages.hostedPagesProfileReceiptsOverview.feeReason.${invoice.feeReason}`,
                )}
              </div>
              <div className="text-lg font-semibold">
                {formatCurrency(invoice.totalAmount, settings.defaultCurrency)}
              </div>
            </div>
          )}
          {!!invoice && (
            <div className="dark:text-gray-25 flex w-full flex-row items-center justify-between text-gray-950">
              <div className="text-xl font-bold">
                {t("pages.hostedPagesProfileReceiptsOverview.totalAmount")}
              </div>
              <div className="text-xl font-bold">
                {formatCurrency(invoice.totalAmount, settings.defaultCurrency)}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
